import type { RecurringRevenueReport } from '@vy/types';
import type {
  ContractsByCustomer,
  CustomerMrrByMonth,
  RowWithMonthlyMrr,
} from './types';

type MonthlyBreakdown = {
  totalMrr: number;
};

export function prepareRows(
  report: RecurringRevenueReport,
  monthKeys: string[],
): RowWithMonthlyMrr[] {
  return report.map((row) => prepareRow(row, monthKeys));
}

export function buildContractsByCustomer(rows: RowWithMonthlyMrr[]): ContractsByCustomer {
  const map: ContractsByCustomer = new Map();

  for (const row of rows) {
    const existing = map.get(row.customerId) ?? { contractStarts: [], contractEnds: [] };
    existing.contractStarts.push(row.contractStartDate);
    existing.contractEnds.push(row.contractEndDate);
    map.set(row.customerId, existing);
  }

  return map;
}

export function buildCustomerMrrByMonth(
  rows: RowWithMonthlyMrr[],
  monthKeys: string[],
): CustomerMrrByMonth {
  const result: CustomerMrrByMonth = new Map();

  for (const row of rows) {
    const monthlyForCustomer = result.get(row.customerId) ?? new Map<string, number>();

    for (const key of monthKeys) {
      const prior = monthlyForCustomer.get(key) ?? 0;
      monthlyForCustomer.set(key, prior + (row.monthlyMrr.get(key) ?? 0));
    }

    result.set(row.customerId, monthlyForCustomer);
  }

  return result;
}

function prepareRow(row: RecurringRevenueReport[number], monthKeys: string[]): RowWithMonthlyMrr {
  const monthlyMrr = new Map<string, number>();

  for (const key of monthKeys) {
    monthlyMrr.set(key, extractMonthlyMrr(row, key));
  }

  return {
    customerId: row.customerId ?? row.contractId,
    contractStartDate: new Date(row.contractStartDate),
    contractEndDate: row.contractEndDate ? new Date(row.contractEndDate) : null,
    monthlyMrr,
  };
}

function extractMonthlyMrr(row: RecurringRevenueReport[number], monthKey: string): number {
  const value = (row as Record<string, unknown>)[monthKey];

  if (value && typeof value === 'object' && 'totalMrr' in value) {
    return (value as MonthlyBreakdown).totalMrr || 0;
  }

  if (typeof value === 'number') {
    return value;
  }

  return 0;
}
